import { Link } from "@tanstack/react-router"
import { useEffect, useState } from "react"

import { trpc } from "../lib/trpc"

interface BookGenerationForm {
  prompt: string
  pageCount: number
  generateImages: boolean
  imageModel: string
  imageSize: string
  imageQuality: string
  artStyle: string
}

interface GeneratedBook {
  id: string
  title: string
  status: string
  createdAt: string
  pageCount: number
  hasImages: boolean
}

interface ImageModelOption {
  value: string
  label: string
  provider: string
  rateUSD: number
  category: "standard" | "premium" | "free"
}

interface SizeOption {
  value: string
  label: string
  description: string
}

interface QualityOption {
  value: string
  label: string
  description: string
}

interface ArtStyleOption {
  value: string
  name: string
  description: string
  category: string
  icon: string
}

/**
 * Children's Books Screen - AI-Powered Book Generation
 *
 * Features:
 * - Generate children's books with custom prompts
 * - Configure AI models for text and image generation
 * - Adjust page count, image size, and quality settings
 * - View generated books library
 * - Book viewer with pages and illustrations
 */
function ChildrenBooksScreen() {
  const [activeTab, setActiveTab] = useState<"generate" | "library">("generate")
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedBooks, setGeneratedBooks] = useState<GeneratedBook[]>([])

  const [form, setForm] = useState<BookGenerationForm>({
    prompt: "",
    pageCount: 8,
    generateImages: true,
    imageModel: "dall-e-3",
    imageSize: "1024x1024",
    imageQuality: "standard",
    artStyle: "",
  })

  // tRPC hooks
  const { data: imageOptions, isLoading: isLoadingOptions } = trpc.childrenBooks.getImageGenerationOptions.useQuery()
  const { data: artStylesData, isLoading: isLoadingStyles } = trpc.childrenBooks.getArtStyles.useQuery()
  const {
    data: booksData,
    isLoading: isLoadingBooks,
    refetch,
  } = trpc.childrenBooks.listBooks.useQuery({
    limit: 50,
    offset: 0,
  })

  const generateBookMutation = trpc.childrenBooks.generateBook.useMutation({
    onSuccess: () => {
      void refetch()
      setActiveTab("library")
      setIsGenerating(false)
    },
    onError: (error) => {
      console.error("Failed to generate book:", error)
      alert(`Failed to generate book: ${error.message}`)
      setIsGenerating(false)
    },
  })

  // Update books when data changes
  useEffect(() => {
    if (booksData?.success && booksData.books) {
      setGeneratedBooks(
        booksData.books.map((book) => ({
          id: book.id,
          title: book.title,
          status: book.generationStatus,
          createdAt: new Date(book.createdAt).toISOString(),
          pageCount: book.pageCount,
          hasImages: Boolean(book.bookData),
        })),
      )
    }
  }, [booksData])

  // Set default model when options load
  useEffect(() => {
    if (imageOptions?.success && imageOptions.options.models.length > 0) {
      const defaultModel = imageOptions.options.recommended.primary || imageOptions.options.models[0]?.value
      if (defaultModel) {
        setForm((prev) => ({ ...prev, imageModel: defaultModel }))
      }
    }
  }, [imageOptions])

  // Set default art style when styles load
  useEffect(() => {
    if (artStylesData?.success && artStylesData.styles.length > 0 && !form.artStyle) {
      const defaultStyle = artStylesData.recommended.beginner || artStylesData.styles[0]?.value
      if (defaultStyle) {
        setForm((prev) => ({ ...prev, artStyle: defaultStyle }))
      }
    }
  }, [artStylesData, form.artStyle])

  const imageModels: ImageModelOption[] = imageOptions?.success ? imageOptions.options.models : []
  const imageSizes: SizeOption[] = imageOptions?.success ? imageOptions.options.sizes : []
  const imageQualities: QualityOption[] = imageOptions?.success ? imageOptions.options.qualities : []
  const artStyles: ArtStyleOption[] = artStylesData?.success ? artStylesData.styles : []
  const artStyleCategories = artStylesData?.success ? artStylesData.categories : {}

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!form.prompt.trim()) {
      alert("Please enter a story prompt")
      return
    }

    setIsGenerating(true)

    try {
      await generateBookMutation.mutateAsync({
        prompt: form.prompt,
        pageCount: form.pageCount,
        generateImages: form.generateImages,
        imageModel: form.imageModel as any, // Type assertion for now
        imageSize: form.imageSize as any,
        imageQuality: form.imageQuality as any,
        artStyle: form.artStyle as any,
      })
    } catch (error) {
      // Error handled in onError callback
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return "✅"
      case "generating_text":
        return "📝"
      case "generating_images":
        return "🎨"
      case "failed":
        return "❌"
      default:
        return "⏳"
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case "completed":
        return "Completed"
      case "generating_text":
        return "Generating Text..."
      case "generating_images":
        return "Generating Images..."
      case "failed":
        return "Failed"
      default:
        return "Pending"
    }
  }

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case "free":
        return <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Free</span>
      case "premium":
        return <span className="ml-2 px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">Premium</span>
      default:
        return <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">Standard</span>
    }
  }

  if (isLoadingOptions || isLoadingStyles) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <div className="text-center py-8">
          <div className="text-gray-500">Loading AI models and art styles...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <header className="mb-8">
        <h1 className="text-4xl font-bold  mb-2">📚 AI Children&apos;s Book Generator</h1>
        <p className=" text-lg">Create magical children&apos;s books with AI-generated stories and illustrations</p>
      </header>

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab("generate")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "generate" ? "border-blue-500 " : "border-transparent text-gray-500 hover:text-primary"
            }`}
          >
            ✨ Generate New Book
          </button>
          <button
            onClick={() => setActiveTab("library")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "library" ? "border-blue-500 " : "border-transparent text-gray-500 hover:text-primary"
            }`}
          >
            📖 Book Library ({generatedBooks.length})
          </button>
        </nav>
      </div>

      {/* Generate Tab */}
      {activeTab === "generate" && (
        <div className="bg-background rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-semibold mb-6">Create Your Children&apos;s Book</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Story Prompt */}
            <div>
              <label htmlFor="prompt" className="block text-sm font-medium text-primary mb-2">
                Story Prompt *
              </label>
              <textarea
                id="prompt"
                required
                value={form.prompt}
                onChange={(e) => setForm((prev) => ({ ...prev, prompt: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe the story you want to create... e.g., 'A brave little mouse who dreams of becoming a knight and goes on an adventure to save the kingdom from a friendly dragon'"
              />
              <p className="mt-1 text-sm text-gray-500">
                Describe the characters, setting, and adventure you want in your story
              </p>
            </div>

            {/* Page Count */}
            <div>
              <label htmlFor="pageCount" className="block text-sm font-medium text-primary mb-2">
                Number of Pages
              </label>
              <input
                type="range"
                id="pageCount"
                min="1"
                max="20"
                value={form.pageCount}
                onChange={(e) => setForm((prev) => ({ ...prev, pageCount: Number.parseInt(e.target.value) }))}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-500 mt-1">
                <span>1 page</span>
                <span className="font-medium">{form.pageCount} pages</span>
                <span>20 pages</span>
              </div>
            </div>

            {/* Generate Images Toggle */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="generateImages"
                checked={form.generateImages}
                onChange={(e) => setForm((prev) => ({ ...prev, generateImages: e.target.checked }))}
                className="h-4 w-4  focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="generateImages" className="ml-2 text-sm text-primary">
                Generate AI illustrations for each page
              </label>
            </div>

            {/* Art Style Selection (conditional) */}
            {form.generateImages && (
              <div className="space-y-4 p-4 bg-foregroundx rounded-md">
                <h3 className="text-lg font-medium ">Art Style</h3>

                <fieldset>
                  <legend className="block text-sm font-medium text-primary mb-3">
                    Choose the artistic style for your book illustrations
                  </legend>

                  {/* Group styles by category */}
                  <div className="space-y-6">
                    {Object.entries(artStyleCategories).map(([category, styles]) => (
                      <div key={category}>
                        <h4 className="text-sm font-medium  mb-2">{category}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {styles.map((style: ArtStyleOption) => (
                            <label
                              key={style.value}
                              className="flex items-start p-3 border border-border rounded-lg  cursor-pointer"
                            >
                              <input
                                type="radio"
                                name="artStyle"
                                value={style.value}
                                checked={form.artStyle === style.value}
                                onChange={(e) => setForm((prev) => ({ ...prev, artStyle: e.target.value }))}
                                className="h-4 w-4  focus:ring-blue-500 mt-1"
                              />
                              <div className="ml-3 flex-1">
                                <div className="flex items-center">
                                  <span className="text-lg mr-2">{style.icon}</span>
                                  <div className="text-sm font-medium ">{style.name}</div>
                                </div>
                                <div className="text-sm text-gray-500 mt-1">{style.description}</div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </fieldset>
              </div>
            )}

            {/* Image Settings (conditional) */}
            {form.generateImages && (
              <div className="space-y-4 p-4 bg-foregroundx rounded-md">
                <h3 className="text-lg font-medium ">Image Generation Settings</h3>

                {/* AI Model Selection */}
                <fieldset>
                  <legend className="block text-sm font-medium text-primary mb-2">AI Model</legend>
                  <div className="space-y-2">
                    {imageModels.map((model) => (
                      <label key={model.value} className="flex items-center">
                        <input
                          type="radio"
                          name="imageModel"
                          value={model.value}
                          checked={form.imageModel === model.value}
                          onChange={(e) => setForm((prev) => ({ ...prev, imageModel: e.target.value }))}
                          className="h-4 w-4  focus:ring-blue-500"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center">
                            <div className="text-sm font-medium ">{model.label}</div>
                            {getCategoryBadge(model.category)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {model.provider} • ${model.rateUSD.toFixed(3)} per image
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </fieldset>

                {/* Image Size */}
                <div>
                  <label htmlFor="imageSize" className="block text-sm font-medium text-primary mb-2">
                    Image Size
                  </label>
                  <select
                    id="imageSize"
                    value={form.imageSize}
                    onChange={(e) => setForm((prev) => ({ ...prev, imageSize: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden
                             focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {imageSizes.map((size) => (
                      <option key={size.value} value={size.value}>
                        {size.label} - {size.description}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Image Quality */}
                <fieldset>
                  <legend className="block text-sm font-medium text-primary mb-2">Image Quality</legend>
                  <div className="space-y-2">
                    {imageQualities.map((quality) => (
                      <label key={quality.value} className="flex items-center">
                        <input
                          type="radio"
                          name="imageQuality"
                          value={quality.value}
                          checked={form.imageQuality === quality.value}
                          onChange={(e) => setForm((prev) => ({ ...prev, imageQuality: e.target.value }))}
                          className="h-4 w-4  focus:ring-blue-500"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium ">{quality.label}</div>
                          <div className="text-sm text-gray-500">{quality.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>
            )}

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isGenerating || !form.prompt.trim()}
                className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700
                         disabled:opacity-50 disabled:cursor-not-allowed
                         focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                         transition-colors duration-200"
              >
                {isGenerating ? "✨ Creating Your Book..." : "🚀 Generate Children's Book"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Library Tab */}
      {activeTab === "library" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">Your Book Library</h2>
            <button
              onClick={() => setActiveTab("generate")}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700
                       focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                       transition-colors duration-200"
            >
              ➕ Create New Book
            </button>
          </div>

          {/* Books List */}
          <div className="space-y-4">
            {isLoadingBooks ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Loading books...</div>
              </div>
            ) : generatedBooks.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                <div className="text-gray-500 text-lg mb-2">No books created yet</div>
                <button onClick={() => setActiveTab("generate")} className=" hover:text-blue-700 font-medium">
                  Create your first children&apos;s book
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {generatedBooks.map((book) => (
                  <div
                    key={book.id}
                    className="bg-background rounded-lg shadow-lg p-6 border border-border hover:shadow-xl transition-shadow"
                  >
                    {/* Book Cover Placeholder */}
                    <div className="w-full h-48 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg mb-4 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-4xl mb-2">📖</div>
                        <div className="text-sm ">{book.hasImages ? "With Illustrations" : "Text Only"}</div>
                      </div>
                    </div>

                    {/* Book Info */}
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold  line-clamp-2">{book.title}</h3>

                      <div className="flex items-center text-sm text-gray-500">
                        <span>{getStatusIcon(book.status)}</span>
                        <span className="ml-2">{getStatusText(book.status)}</span>
                      </div>

                      <div className="text-sm text-gray-500">
                        {book.pageCount} pages • Created {new Date(book.createdAt).toLocaleDateString()}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-3">
                        {book.status === "completed" ? (
                          <>
                            <Link
                              // @ts-ignore
                              to="/book/$bookId"
                              // @ts-ignore
                              params={{ bookId: book.id }}
                              className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors text-center"
                            >
                              📖 Read Book
                            </Link>
                            <button className="px-3 py-2 border border-gray-300 text-primary text-sm rounded-md  transition-colors">
                              📥 Download
                            </button>
                          </>
                        ) : (
                          <button className="flex-1 px-3 py-2 bg-foregroundx text-gray-500 text-sm rounded-md cursor-not-allowed">
                            ⏳ Generating...
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="mt-12 p-6 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">💡 Tips for Great Children&apos;s Books</h3>
        <div className="text-sm text-blue-800 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p>
                <strong>Story Elements:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Include relatable characters (animals, children)</li>
                <li>Add a clear problem and solution</li>
                <li>Use simple, engaging language</li>
                <li>Include a positive message or lesson</li>
              </ul>
            </div>
            <div>
              <p>
                <strong>Art Style Guide:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Watercolor:</strong> Soft, dreamy stories
                </li>
                <li>
                  <strong>Cartoon:</strong> Fun, energetic adventures
                </li>
                <li>
                  <strong>Digital Fantasy:</strong> Magical tales
                </li>
                <li>
                  <strong>Vintage:</strong> Classic, timeless stories
                </li>
                <li>
                  <strong>Minimalist:</strong> Simple, modern concepts
                </li>
              </ul>
            </div>
            <div>
              <p>
                <strong>Best Practices:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Start with 4-8 pages for shorter stories</li>
                <li>DALL-E 3 produces the highest quality images</li>
                <li>Square images work best for covers</li>
                <li>Portrait images are great for full-page scenes</li>
                <li>Match art style to story mood</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChildrenBooksScreen
